import { useEffect, useMemo, useState } from 'react'

import {
  resetCheckboxTableStore,
  setCheckboxTableStore,
  useTableCheckboxStore
} from '@/stores/useTableCheckboxStore'
import { warnDev } from '@/utils/warnDev'

import { TableBase } from '../TableBase/TableBase'
import { FilterToolbar } from './components/FilterToolbar'
import type { FilterableTableProps, FilterType, SelectedFilters } from './FilterableTable.types'

export const FilterableTable = <
  Data extends Record<string, unknown>,
  SortAttribute extends string,
  FilterParams extends string = string
>({
  data,
  filters,
  tableId,
  selectedFilters,
  persistSelection,
  clientSideFilters,
  defaultSelectedFilters,
  onRowCheck,
  onFiltersChange,
  ...props
}: FilterableTableProps<Data, SortAttribute, FilterParams>) => {
  const selectedRows = useTableCheckboxStore((state) => state[tableId || '']) as
    | Array<Data & { __id: string }>
    | undefined
  const [internalSelectedFilters, setInternalSelectedFilters] = useState<
    SelectedFilters<FilterParams>
  >(defaultSelectedFilters || {})
  const selectedFiltersToUse = clientSideFilters ? internalSelectedFilters : selectedFilters

  const dataToUseInTable = useMemo(() => {
    if (!clientSideFilters || !data || !filters) return data

    return data.filter((item) => {
      return Object.entries(internalSelectedFilters).every((pair) => {
        const [key, filterValue] = pair as [FilterParams, FilterType[] | undefined]

        const filter = filters.find((f) => f.paramName === key)

        if (!filter || filterValue === undefined) return true

        if (!filter.filterOptions) {
          warnDev(
            `For client-side filtering, filter.filterOptions is required for filter with paramName: ${key}`
          )
          return false
        }

        if (filter.type === 'freeTextSearch') {
          return filter.filterOptions(item, `${filterValue[0].value}`)
        }

        if (filter.type === 'select' && filter.multiple) {
          return filter.filterOptions(item, filterValue)
        }

        if (filter.type === 'date') {
          return filter.filterOptions(item, new Date(filterValue[0].value as string))
        }

        if (filter.type === 'dateRange') {
          return filter.filterOptions(item, new Date(filterValue[0].value as string))
        }

        return filter.filterOptions(item, filterValue)
      })
    })
  }, [data, internalSelectedFilters, clientSideFilters, filters])

  const selectedRowsRecord = useMemo(() => {
    if (!selectedRows) return {}
    return selectedRows.reduce(
      (acc, row) => {
        acc[row.__id] = true
        return acc
      },
      {} as Record<string, boolean>
    )
  }, [selectedRows])

  useEffect(() => {
    if (!persistSelection && tableId) {
      return () => {
        resetCheckboxTableStore(tableId)
      }
    }
  }, [persistSelection, tableId])

  const handleSelect: typeof onRowCheck = (rows) => {
    if (tableId) {
      setCheckboxTableStore(tableId || '', rows)
    }
    onRowCheck?.(rows)
  }

  const handleFiltersChange = (newSelectedFilters: SelectedFilters) => {
    if (clientSideFilters) {
      setInternalSelectedFilters(newSelectedFilters)
    }

    onFiltersChange?.(newSelectedFilters)
  }

  return (
    <>
      {filters && (
        <FilterToolbar
          filters={filters}
          selectedFilters={selectedFiltersToUse}
          onFiltersChange={handleFiltersChange}
        />
      )}

      <TableBase
        {...props}
        // TO DO: Remove this cast when the type is fixed in TableBase
        tableId={tableId as string}
        data={dataToUseInTable}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selectedRows={props.withCheckboxes ? (selectedRowsRecord as any) : {}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onRowCheck={props?.withCheckboxes ? (handleSelect as any) : undefined}
      />
    </>
  )
}
